// src/pages/admin/EditPermissionsPage.tsx

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { useAdminStore } from '../../store/adminStore';
import { AccessPolicyService } from '../../services/accessPolicyService';
import { policyToSelection, grantAllSelection, toggleYear, toggleEvent } from '../../utils/folderTreeUtils';
import type { FolderSelection, FolderTreeNode } from '../../models/adminTypes';

const accessPolicyService = new AccessPolicyService();

export function EditPermissionsPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const session = useAppStore((s) => s.session);
  const awsCredentials = useAppStore((s) => s.awsCredentials);

  const folderTree = useAdminStore((s) => s.folderTree);
  const folderTreeLoading = useAdminStore((s) => s.folderTreeLoading);
  const fetchFolderTree = useAdminStore((s) => s.fetchFolderTree);
  const savingPermissions = useAdminStore((s) => s.savingPermissions);
  const saveError = useAdminStore((s) => s.saveError);
  const saveSuccess = useAdminStore((s) => s.saveSuccess);
  const savePermissions = useAdminStore((s) => s.savePermissions);
  const clearSaveStatus = useAdminStore((s) => s.clearSaveStatus);
  const users = useAdminStore((s) => s.users);

  const [selection, setSelection] = useState<FolderSelection | null>(null);
  const [loadingPolicy, setLoadingPolicy] = useState(true);
  const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());

  const idToken = session?.idToken || '';

  // Fetch folder tree and current policy
  useEffect(() => {
    if (awsCredentials) {
      fetchFolderTree(awsCredentials);
    }
  }, [awsCredentials, fetchFolderTree]);

  useEffect(() => {
    if (!userId || !idToken || folderTree.length === 0) return;

    const loadPolicy = async () => {
      setLoadingPolicy(true);
      try {
        const policy = await accessPolicyService.fetchPolicy(idToken, userId);
        setSelection(policyToSelection(policy, folderTree));
      } catch {
        // No policy found — start with empty selection
        setSelection(policyToSelection(null, folderTree));
      }
      setLoadingPolicy(false);
    };
    loadPolicy();
  }, [userId, idToken, folderTree]);

  // Clean up save status on unmount
  useEffect(() => {
    return () => clearSaveStatus();
  }, [clearSaveStatus]);

  const handleGrantAllToggle = () => {
    if (!selection) return;
    if (selection.grantAll) {
      // Turn off grant-all — clear everything
      setSelection({ grantAll: false, selectedYears: new Set(), selectedEvents: new Map() });
    } else {
      setSelection(grantAllSelection(folderTree));
    }
  };

  const handleToggleYear = (year: string, allEvents: string[]) => {
    if (!selection) return;
    setSelection(toggleYear(selection, year, allEvents));
  };

  const handleToggleEvent = (year: string, event: string, allEventsInYear: string[]) => {
    if (!selection) return;
    setSelection(toggleEvent(selection, year, event, allEventsInYear));
  };

  const handleExpandYear = (year: string) => {
    setExpandedYears((prev) => {
      const next = new Set(prev);
      if (next.has(year)) {
        next.delete(year);
      } else {
        next.add(year);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!selection || !userId) return;
    await savePermissions(idToken, userId, selection);
  };

  const isYearFullySelected = (node: FolderTreeNode): boolean => {
    if (!selection) return false;
    if (selection.grantAll) return true;
    return selection.selectedYears.has(node.year);
  };

  const isYearPartiallySelected = (node: FolderTreeNode): boolean => {
    if (!selection) return false;
    if (selection.grantAll) return false;
    if (selection.selectedYears.has(node.year)) return false;
    const events = selection.selectedEvents.get(node.year);
    return !!events && events.size > 0 && events.size < node.events.length;
  };

  const isEventSelected = (year: string, event: string): boolean => {
    if (!selection) return false;
    if (selection.grantAll) return true;
    if (selection.selectedYears.has(year)) return true;
    const events = selection.selectedEvents.get(year);
    return !!events && events.has(event);
  };

  if (folderTreeLoading || loadingPolicy) {
    return (
      <div className="years-page" role="status" aria-label="Loading permissions">
        <div className="loading-indicator">Loading folder structure…</div>
      </div>
    );
  }

  return (
    <div className="years-page">
      <div className="years-header">
        <h1>📁 Edit Permissions</h1>
        <button
          className="refresh-button"
          onClick={() => navigate('/admin')}
          style={{ minHeight: '44px' }}
        >
          ← Back
        </button>
      </div>

      <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
        User: <strong>{users.find(u => u.userId === userId)?.email ?? userId}</strong>
        {' '}
        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>({userId})</span>
      </p>

      {/* Grant All Toggle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem',
        padding: '1rem',
        background: 'var(--gradient-card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
      }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={selection?.grantAll ?? false}
            onChange={handleGrantAllToggle}
            style={{ width: '1.2rem', height: '1.2rem', accentColor: 'var(--primary)' }}
          />
          <span style={{ fontWeight: 600, color: 'var(--text)' }}>
            Grant access to all folders
          </span>
        </label>
      </div>

      {/* Folder Tree */}
      {!selection?.grantAll && (
        <div style={{ marginBottom: '1.5rem' }}>
          {folderTree.length === 0 ? (
            <p className="empty-state">No folders found in S3</p>
          ) : (
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {folderTree.map((node) => (
                <li key={node.year}>
                  {/* Year row */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1rem',
                    background: 'var(--gradient-card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-md)',
                  }}>
                    <input
                      type="checkbox"
                      checked={isYearFullySelected(node)}
                      ref={(el) => {
                        if (el) el.indeterminate = isYearPartiallySelected(node);
                      }}
                      onChange={() => handleToggleYear(node.year, node.events)}
                      style={{ width: '1.1rem', height: '1.1rem', accentColor: 'var(--primary)' }}
                      aria-label={`Select all events in ${node.year}`}
                    />
                    <button
                      onClick={() => handleExpandYear(node.year)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: '0',
                        minHeight: 'auto',
                        minWidth: 'auto',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        color: 'var(--text-secondary)',
                      }}
                      aria-label={expandedYears.has(node.year) ? `Collapse ${node.year}` : `Expand ${node.year}`}
                    >
                      {expandedYears.has(node.year) ? '▼' : '▶'}
                    </button>
                    <span style={{ fontWeight: 600, color: 'var(--text)' }}>
                      📅 {node.year}
                    </span>
                    <span style={{
                      fontSize: '0.8rem',
                      color: 'var(--text-secondary)',
                      marginLeft: 'auto',
                    }}>
                      {node.events.length} event{node.events.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Events (expanded) */}
                  {expandedYears.has(node.year) && (
                    <ul style={{
                      listStyle: 'none',
                      marginLeft: '2rem',
                      marginTop: '0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.25rem',
                    }}>
                      {node.events.map((event) => (
                        <li key={event}>
                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            padding: '0.4rem 0.75rem',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            transition: 'background var(--transition-fast)',
                          }}>
                            <input
                              type="checkbox"
                              checked={isEventSelected(node.year, event)}
                              onChange={() => handleToggleEvent(node.year, event, node.events)}
                              style={{ width: '1rem', height: '1rem', accentColor: 'var(--primary)' }}
                            />
                            <span style={{ fontSize: '0.9rem', color: 'var(--text)' }}>
                              {event}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Save bar */}
      <div style={{
        position: 'sticky',
        bottom: 0,
        padding: '1rem',
        background: 'var(--surface)',
        borderTop: '1px solid var(--border)',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-md)',
      }}>
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={savingPermissions || !selection}
          style={{ minHeight: '44px' }}
        >
          {savingPermissions ? 'Saving…' : '💾 Save Permissions'}
        </button>

        {saveSuccess && (
          <span style={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.9rem' }}>
            ✓ Saved successfully
          </span>
        )}
        {saveError && (
          <span style={{ color: 'var(--error)', fontWeight: 500, fontSize: '0.9rem' }}>
            {saveError}
          </span>
        )}
      </div>
    </div>
  );
}
